import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// In-memory cache for translations to reduce API calls
const translationCache = new Map();

/**
 * Hook to translate property content based on current language
 * @param {Object} property - Property object with title, description, etc.
 * @returns {Object} - { translatedTitle, translatedDescription, isLoading }
 */
export const usePropertyTranslation = (property) => {
  const { i18n } = useTranslation();
  const [translatedTitle, setTranslatedTitle] = useState(property?.title || '');
  const [translatedDescription, setTranslatedDescription] = useState(property?.description || '');
  const [isLoading, setIsLoading] = useState(false);

  // R0-1 FIX: Check both 'language' and 'preferredLanguage' localStorage keys
  const getLanguage = () => {
    const langKey = localStorage.getItem('language');
    const prefLangKey = localStorage.getItem('preferredLanguage');
    const i18nLang = i18n.language;
    
    if (langKey === 'pt' || langKey === 'pt-BR') return 'pt';
    if (prefLangKey === 'pt-BR') return 'pt';
    if (i18nLang === 'pt-BR' || i18nLang === 'pt') return 'pt';
    return 'en';
  };

  const currentLang = getLanguage();

  useEffect(() => {
    if (!property?.id) return;

    // Reset to original values first
    setTranslatedTitle(property.title || '');
    setTranslatedDescription(property.description || '');

    const translateContent = async () => {
      const cacheKey = `${property.id}-${currentLang}`;
      
      // Check cache first
      if (translationCache.has(cacheKey)) {
        const cached = translationCache.get(cacheKey);
        setTranslatedTitle(cached.title);
        setTranslatedDescription(cached.description);
        return;
      }

      // P2-26 Fix: Skip translation if content appears to already be in target language
      // Most property content is in Portuguese, so skip if target is PT
      if (currentLang === 'pt') {
        // Cache the original values
        translationCache.set(cacheKey, {
          title: property.title || '',
          description: property.description || ''
        });
        return;
      }

      // Only translate if language is different from the content language
      // We'll let the backend auto-detect the source language
      setIsLoading(true);
      
      try {
        // Use batch translation for efficiency
        const response = await fetch(`${API_URL}/api/translate/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            texts: [property.title || '', property.description || ''].filter(Boolean),
            source_lang: 'auto',
            target_lang: currentLang,
            auto_detect: true
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const translations = data.translations || [];
          
          const newTitle = translations[0]?.translated || property.title;
          const newDescription = translations[1]?.translated || property.description;
          
          // Update state
          setTranslatedTitle(newTitle);
          setTranslatedDescription(newDescription);
          
          // Cache the result
          translationCache.set(cacheKey, {
            title: newTitle,
            description: newDescription
          });
        }
      } catch (error) {
        console.error('Translation error:', error);
        // Keep original values on error
      } finally {
        setIsLoading(false);
      }
    };

    translateContent();
  }, [property?.id, property?.title, property?.description, currentLang]);

  return {
    translatedTitle,
    translatedDescription,
    isLoading,
    originalTitle: property?.title || '',
    originalDescription: property?.description || ''
  };
};

/**
 * Hook to translate a single text
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language ('en' or 'pt')
 * @returns {Object} - { translatedText, isLoading }
 */
export const useTextTranslation = (text, targetLang = null) => {
  const { i18n } = useTranslation();
  const [translatedText, setTranslatedText] = useState(text || '');
  const [isLoading, setIsLoading] = useState(false);

  // R0-1 FIX: Check both 'language' and 'preferredLanguage' localStorage keys
  // since the app uses different keys in different places
  const getLanguage = () => {
    const langKey = localStorage.getItem('language');
    const prefLangKey = localStorage.getItem('preferredLanguage');
    const i18nLang = i18n.language;
    
    // Prioritize: explicit targetLang > localStorage 'language' > localStorage 'preferredLanguage' > i18n
    if (targetLang) return targetLang;
    if (langKey === 'pt' || langKey === 'pt-BR') return 'pt';
    if (prefLangKey === 'pt-BR') return 'pt';
    if (i18nLang === 'pt-BR' || i18nLang === 'pt') return 'pt';
    return 'en';
  };

  const lang = getLanguage();

  useEffect(() => {
    if (!text) {
      setTranslatedText('');
      return;
    }

    // R0-1 FIX: When language is PT, return original text immediately
    // since most content is already in Portuguese
    if (lang === 'pt') {
      setTranslatedText(text);
      setIsLoading(false);
      return;
    }

    const cacheKey = `text-${text.substring(0, 50)}-${lang}`;
    
    // Check cache first
    if (translationCache.has(cacheKey)) {
      setTranslatedText(translationCache.get(cacheKey));
      return;
    }

    const translateText = async () => {
      setIsLoading(true);
      
      try {
        const response = await fetch(`${API_URL}/api/translate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text,
            source_lang: 'auto',
            target_lang: lang,
            auto_detect: true
          }),
        });

        if (response.ok) {
          const data = await response.json();
          const translated = data.translated || text;
          setTranslatedText(translated);
          translationCache.set(cacheKey, translated);
        } else {
          // On error, use original text
          setTranslatedText(text);
        }
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedText(text);
      } finally {
        setIsLoading(false);
      }
    };

    translateText();
  }, [text, lang]);

  return { translatedText, isLoading };
};

/**
 * Clear translation cache (useful when language changes)
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

export default usePropertyTranslation;
